import type { I18nText } from "./locales";

/**
 * spin 盘面。一格一条，`code` 从 1 顺延，前后端通讯用 code 不用名字。
 *
 * 运行规则（出现层保底/冷却、金额层收敛）：https://portwind.jp.larksuite.com/wiki/AqXwwQ3HNiBbQkkzU8AjI1nLpCh
 *
 * 一格**可以同时发多种奖励**（金币 + 骰子、盾牌 + 金币），也可以是「进入某个副玩法格」。
 *
 * 金币的单位是 **B 值**，不是绝对金币：实际发放 = B 值 × 关卡 bValue。
 * 填绝对金币会绕开整套预算体系（目标骰次 × 每骰产出 × B 值反推关卡金币总量）。
 *
 * 「区间」不是掷骰概率：引擎按本关剩余预算与进度在 min ~ max 内选点，
 * 玩家体验是随机，总量收敛到关卡预算。骰子同理，计入通关目标骰次的骰子预算。
 */
/** blank = 空白格：踩中什么都不发，只占盘面概率；一格里它必须是唯一一条 */
export type SpinRewardKind =
  | "coin" | "dice" | "shield" | "subgame" | "item_card" | "free" | "wildcard" | "blank"
  /**
   * 领养点。`ref` 是 `shop`（收养馆）/ `grass`（草丛），不是某一只宠物——
   * 具体抽哪只由领养池决定。
   *
   * **本页的多选框还列不出它**（管端 UI 等设计稿）。但类型里必须有，
   * 否则 BoardPage 那个「保留 UI 不认识的种类」的判断会因为类型收窄而写不出来，
   * 运营编辑同一格的别的奖励时 pet 会被静默删掉。
   */
  | "pet"
  /**
   * idol 拼图碎片（《棋盘56格定稿V1.3》第 12/40 格）。定值片数，**无 ref**——
   * 碎片是通用余额，兑给哪个 idol 由玩家在写真馆选，不在这里配。
   */
  | "puzzle_frag"
  /**
   * 钻石（《奖励落点配置 V1.8》「留白清单(幸运草承接)」：小额 5-20、必须稀有）。
   * **收区间**（fixed | range），因为产品给的是区间不是定值；无 ref。
   *
   * 它**没有专属棋盘格是有意的**——留白清单的东西由客户端走到幸运草开箱表现，
   * 服务端这边它就是一条低权重掉落项。别在这个页面找「哪一格掉钻石」。
   */
  | "gems"
  /**
   * 道具格（多人专用）。开关型、无 ref、无数量：踩中从本局道具基池（在场玩家已解锁道具卡的并集）三选一。
   * 只有多人代理能配，通常与金币多选（docs/room-protocol-ludo.md 第四节）。
   */
  | "ability_item"
  /** 遣回家（多人转盘的负面扇区）：落在这里的这一枚棋子退回出场区。只能单独一条、只有多人代理能配 */
  | "send_home"
  /**
   * 穿越（单人棋盘）：掷骰返回两个随机数 detail.warp {r100, steps}，不发资产。
   * 2026-10-05 起不能再配成掉落项（改成表格下面的「穿越概率」），只为让存量行能被看见、取消勾选
   */
  | "warp"
  /**
   * 副玩法组（2026-10-05）：ref 是 A–E。抽中后客户端在组内就近挑一种副玩法领；组成员在副玩法页配。
   * 只在「客户端选副玩法」打开时成立，单人代理专用
   */
  | "subgame_group"
  /**
   * 活动道具掉落项（《棋盘活动道具收集玩法 V1.1》2.1）。**与 ability_item 不是一回事**：
   * 那个是多人对局抽道具卡，这个是单人棋盘的活动道具收集。
   *
   * **不在这一页配**——基础值、权重与空窗期回退在「副玩法配置 → 活动道具」页上，
   * 那里能同时看到档位与节奏验算；只看盘面配不出合理的基础值。这里把它列成一个
   * 已选项（可取消，见 BoardPage 那段注释），是为了让运营在盘面上看得见这四格不是空的。
   *
   * 无 `ref`：发哪个道具由当期活动的 `act_item_ref` 决定，格子只出基础值。
   */
  | "act_item";

export interface SpinReward {
  kind: SpinRewardKind;
  /** coin / dice / gems：fixed 用 value，range 用 min~max */
  mode?: "fixed" | "range";
  value?: number | null;
  min?: number | null;
  max?: number | null;
  /** coinrain：本项承载子区间（×g 万分比），不是金币 B 值区间 */
  carryMinBps?: number | null;
  carryMaxBps?: number | null;
  /**
   * ability_item（道具格）：三选一的池子 ATK_ONLY / DEF_ONLY / MIXED。
   * 多人棋盘格引用这一行时下发给客户端（2026-09-18 起池子跟着行走，不再由格子类型决定）。不填 = 攻守混
   */
  pool?: string | null;
  /** act_item：空窗期这一条改发什么（coin | dice）与发多少。见 lib/act-event.ts */
  fallbackKind?: string | null;
  fallbackValue?: number | null;
  /**
   * subgame：进入哪个玩法格，存 tile_type_id。
   * item_card：开关型，无 ref——掉哪个包由卡包配置的掉落关卡与出包权重决定（cardpage 为准）。
   * **不存界面上那个中文标签**——标签随多语言和改名而变，拿它当引用迟早断，
   * 而且断得很安静：选项对不上就直接从下拉里消失，没人会收到通知。
   */
  ref?: string | null;
}

export interface SpinCell {
  id: string;
  code: number;
  name: string;
  i18n: I18nText;
  /**
   * 概率百分比（整数）。服务端仍按 weight ÷ 启用格之和抽格，管端约定启用格合计必须是 100，
   * 这样填的数就是概率；合计不是 100 时标红（引擎再按预算修正，所以仍不是最终概率）
   */
  weight: number;
  rewards: SpinReward[];
  enabled: boolean;
}

/** 概率 = 本格权重 ÷ 启用格权重之和。停用格不计入分母 */
export function spinOdds(cells: SpinCell[], c: SpinCell): number | null {
  if (!c.enabled) return null;
  const sum = cells.reduce((t, x) => t + (x.enabled ? Number(x.weight || 0) : 0), 0);
  return sum > 0 ? (Number(c.weight || 0) / sum) * 100 : null;
}

/** 启用格的百分比合计。停用格不计 */
export const spinWeightSum = (cells: SpinCell[]): number =>
  cells.reduce((t, x) => t + (x.enabled ? Number(x.weight || 0) : 0), 0);

/**
 * 区间是引擎的取值边界，min > max 取不出点。
 *
 * **两个都填了才判。** 原先是 `min != null && max != null && min <= max`——
 * 刚把模式切到「区间」时两格都还空着，那一刻它就返回 false，于是运营看到的是
 * 「两个空框 + 红字 min 大于 max」：一句与屏幕上的事实直接矛盾的话。
 * 填了一半同理，那是「还没填完」，不是「填错了」。
 *
 * 没填完由保存时的写入口拦（admin/config-content.ts 会指名是哪一格的哪个字段），
 * 这里只负责「两个数都在、而且真的反了」这一种。
 */
export const rangeValid = (r: SpinReward) =>
  r.mode !== "range" || r.min == null || r.max == null || r.min <= r.max;

export function newSpin(nextCode: number): SpinCell {
  // 本地 id 只给界面认行（改 / 删按它找）。读回的行 id 是 sp<code>，以前新格从 sp101 数起，
  // 已有 code ≥ 101 的格时两格同 id，改一格会连带改到另一格。所以新格用随机的、不会和 sp<数字> 撞
  // 新格默认权重 5，不预设奖励——发什么由策划自己勾，替他猜等于塞假配置
  return { id: `sp-new-${Math.random().toString(36).slice(2, 10)}`, code: nextCode, name: "", i18n: {}, weight: 5, rewards: [], enabled: true };
}

export interface ServerSpinCell {
  code: number; name: string; nameI18n: Record<string, string>;
  weight: number; rewards: SpinReward[]; enabled: boolean;
}

/** 服务端行 → 界面模型。id 由 code 派生，稳定且不必单独存一列 */
export function toSpin(r: ServerSpinCell): SpinCell {
  return {
    id: `sp${r.code}`, code: r.code, name: r.nameI18n?.en ?? r.name ?? "",
    i18n: (r.nameI18n ?? {}) as I18nText,
    weight: Number(r.weight ?? 0), rewards: r.rewards ?? [], enabled: r.enabled,
  };
}

export function toServerSpin(c: SpinCell) {
  return {
    code: c.code, name: c.i18n?.en ?? c.name ?? "", nameI18n: c.i18n ?? {},
    weight: c.weight, rewards: c.rewards, enabled: c.enabled,
  };
}
